import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  MapPin, 
  FileText, 
  Star, 
  Mail, 
  Settings, 
  LogOut,
  Menu,
  X,
  Image,
  Calendar,
  Euro,
  LayoutTemplate,
  Navigation,
  PenLine,
  Users
} from 'lucide-react';
import { useAuth } from '@/App';

const AdminLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
    }
  }, [admin, navigate]);

  if (!admin) return null;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Image, label: 'Hero Slider', path: '/admin/slider' },
    { icon: PenLine, label: 'Inhalte', path: '/admin/inhalte' },
    { icon: LayoutTemplate, label: 'Seiten', path: '/admin/seiten' },
    { icon: Navigation, label: 'Menüs', path: '/admin/menus' },
    { icon: Calendar, label: 'Kalender', path: '/admin/kalender' },
    { icon: Euro, label: 'Preise', path: '/admin/preise' },
    { icon: MapPin, label: 'Standorte', path: '/admin/standorte' },
    { icon: FileText, label: 'Blog-Artikel', path: '/admin/blog' },
    { icon: Star, label: 'Bewertungen', path: '/admin/bewertungen' },
    { icon: Mail, label: 'Anfragen', path: '/admin/anfragen' },
    { icon: Users, label: 'Benutzer', path: '/admin/benutzer' },
    { icon: Settings, label: 'Einstellungen', path: '/admin/einstellungen' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-900 text-white p-4 flex justify-between items-center">
        <span className="font-semibold">Admin Panel</span>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-40 h-screen w-64 bg-slate-900 text-white
          transition-transform duration-300
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_303cab3b-acc1-4381-b3cd-1e06f864619c/artifacts/i70vcgc8_Logo-Roessner-Hell.png"
              alt="Fahrschule Rößner"
              className="h-10 w-auto"
            />
          </Link>
          <p className="text-slate-400 text-sm mt-2">Admin Panel</p>
        </div>

        <nav className="p-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`admin-nav-item rounded-lg ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${item.path.split('/').pop()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              {admin.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <div className="font-medium text-sm">{admin.name}</div>
              <div className="text-slate-400 text-xs">{admin.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="admin-nav-item rounded-lg w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            Abmelden
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
